import { useLang } from '../../i18n/LanguageContext'

const COUNTRY_FLAGS = { NO: '🇳🇴', SE: '🇸🇪', DK: '🇩🇰' }

export default function Step5Review({ data, onBack, onSubmit, submitting, error }) {
  const { t } = useLang()
  const countryCode = COUNTRY_FLAGS[data.countryCode] ? data.countryCode : 'NO'
  const countryNames = t('onboarding.countryNames')
  const layouts = t('onboarding.layouts')
  const layout = layouts.find(l => l.key === (data.template || 'classic')) || layouts[0]
  const sameAsPostal = data.sameAsPostalAddress !== false

  return (
    <div className="wizard__body">
      <h1 className="wizard__title">{t('onboarding.step5Title')}</h1>
      <p className="wizard__subtitle">
        {t('onboarding.step5Subtitle')}
      </p>

      {error && (
        <div className="alert alert--error" style={{ marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      <div className="review">
        <div className="review__section">
          <div className="review__heading">{t('onboarding.reviewOrg')}</div>
          <div className="review__grid">
            <span className="review__label">{t('onboarding.reviewName')}</span>
            <span className="review__value">{data.tenantName}</span>

            <span className="review__label">{t('onboarding.reviewUrl')}</span>
            <span className="review__value">{data.slug}.communitynord.com</span>

            {data.customDomain && (
              <>
                <span className="review__label">{t('onboarding.reviewCustomDomain')}</span>
                <span className="review__value">{data.customDomain}</span>
              </>
            )}

            <span className="review__label">{t('onboarding.reviewCountry')}</span>
            <span className="review__value">{COUNTRY_FLAGS[countryCode]} {countryNames[countryCode] || countryCode}</span>

            <span className="review__label">{t('onboarding.reviewShortName')}</span>
            <span className="review__value">{data.shortName}</span>

            {data.orgNumber && (
              <>
                <span className="review__label">{t('onboarding.reviewOrgNumber')}</span>
                <span className="review__value">{data.orgNumber}</span>
              </>
            )}

            <span className="review__label">{t('onboarding.reviewPlan')}</span>
            <span className="review__value">{data.subscriptionPlan || 'free'}</span>

            <span className="review__label">{t('onboarding.reviewLayout')}</span>
            <span className="review__value">{layout.name}</span>

            {data.tagline && (
              <>
                <span className="review__label">{t('onboarding.reviewTagline')}</span>
                <span className="review__value">{data.tagline}</span>
              </>
            )}

            {(data.logoPreview || data.logoUrl) && (
              <>
                <span className="review__label">{t('onboarding.reviewLogo')}</span>
                <span className="review__value">
                  {data.logoPreview ? (
                    <img
                      src={data.logoPreview}
                      alt="Logo"
                      style={{ maxHeight: 40, maxWidth: 120, objectFit: 'contain', display: 'block' }}
                    />
                  ) : (
                    <span style={{ wordBreak: 'break-all' }}>{data.logoUrl}</span>
                  )}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="review__section">
          <div className="review__heading">{t('onboarding.reviewContact')}</div>
          <div className="review__grid">
            <span className="review__label">{t('onboarding.reviewContactPerson')}</span>
            <span className="review__value">{data.contactName}</span>

            <span className="review__label">{t('onboarding.reviewEmail')}</span>
            <span className="review__value">{data.contactEmail}</span>

            {data.contactPhone && (
              <>
                <span className="review__label">{t('onboarding.reviewPhone')}</span>
                <span className="review__value">{data.contactPhone}</span>
              </>
            )}
          </div>
        </div>

        {(data.postalAddress || data.postalCode || data.postalCity) && (
          <div className="review__section">
            <div className="review__heading">{t('onboarding.reviewPostal')}</div>
            <div className="review__grid">
              {data.postalAddress && (
                <>
                  <span className="review__label">{t('onboarding.reviewAddress')}</span>
                  <span className="review__value">{data.postalAddress}</span>
                </>
              )}
              {(data.postalCode || data.postalCity) && (
                <>
                  <span className="review__label">{t('onboarding.reviewPlace')}</span>
                  <span className="review__value">{[data.postalCode, data.postalCity].filter(Boolean).join(' ')}</span>
                </>
              )}
            </div>
          </div>
        )}

        {!sameAsPostal && (data.visitingAddress || data.visitingPostalCode || data.visitingCity) && (
          <div className="review__section">
            <div className="review__heading">{t('onboarding.reviewVisiting')}</div>
            <div className="review__grid">
              {data.visitingAddress && (
                <>
                  <span className="review__label">{t('onboarding.reviewAddress')}</span>
                  <span className="review__value">{data.visitingAddress}</span>
                </>
              )}
              {(data.visitingPostalCode || data.visitingCity) && (
                <>
                  <span className="review__label">{t('onboarding.reviewPlace')}</span>
                  <span className="review__value">{[data.visitingPostalCode, data.visitingCity].filter(Boolean).join(' ')}</span>
                </>
              )}
            </div>
          </div>
        )}

        <div className="review__section">
          <div className="review__heading">{t('onboarding.reviewAccount')}</div>
          <div className="review__grid">
            <span className="review__label">{t('onboarding.reviewUsername')}</span>
            <span className="review__value">{data.ownerUsername}</span>

            <span className="review__label">{t('onboarding.reviewEmail')}</span>
            <span className="review__value">{data.contactEmail}</span>

            <span className="review__label">{t('onboarding.reviewPassword')}</span>
            <span className="review__value">••••••••</span>

            <span className="review__label">{t('onboarding.reviewRole')}</span>
            <span className="review__value" style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
              {t('onboarding.reviewOwner')}
            </span>
          </div>
        </div>
      </div>

      <div className="wizard__actions">
        <button className="btn btn--secondary" onClick={onBack} disabled={submitting}>
          {t('onboarding.back')}
        </button>
        <button
          className="btn btn--primary btn--lg"
          onClick={onSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <span className="spinner spinner--white" />
              {t('onboarding.creating')}
            </>
          ) : (
            t('onboarding.create')
          )}
        </button>
      </div>
    </div>
  )
}
